import { useMemo } from 'react'
import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { Cart, CartItem } from '../types'
import {
    calculateItemDiscountCodeSavings,
    calculateItemTotalSavings,
    CartItemPricingContext,
    getEffectiveFixedDiscountForCart,
    getEffectiveListPrice,
    getItemAppliedDiscountCodes,
} from '../utils/cartTools'

export interface CartDiscounts {
    saleSavings: number
    discountCodeSavings: number
    totalSavings: number
    totalItemCount: number
    hasSaleSavings: boolean
    hasDiscountCode: boolean
    hasAnySavings: boolean
}

export interface ItemDiscount {
    hasDiscount: boolean
    hasDiscountCode: boolean
    hasSaleSavings: boolean
    comparePrice: number | null
    applicableDiscountCodes: string[]
}

export function useCartDiscounts(
    cart: Cart,
    landingPageLinks?: ApiShopperLandingPageLink[],
    discount?: CartItemPricingContext['discount'],
): CartDiscounts {
    const pricingContext = useMemo(
        (): CartItemPricingContext | undefined =>
            landingPageLinks
                ? { landingPageLinks, discount }
                : discount
                  ? { discount }
                  : undefined,
        [landingPageLinks, discount],
    )

    return useMemo(() => {
        const { totalSavings, discountCodeSavings, totalItemCount } =
            cart.items.reduce(
                (acc, item) => {
                    acc.totalSavings += calculateItemTotalSavings(
                        item,
                        pricingContext,
                    )
                    acc.discountCodeSavings +=
                        calculateItemDiscountCodeSavings(item)
                    acc.totalItemCount += item.quantity
                    return acc
                },
                { totalSavings: 0, discountCodeSavings: 0, totalItemCount: 0 },
            )

        const saleSavings = totalSavings - discountCodeSavings

        // A fixed page discount is subtracted from the cart total, not baked
        // into line prices or allocations, so surface it in the code-savings
        // row here. Returns 0 whenever the savings are already on the lines.
        const pageFixedSavings = getEffectiveFixedDiscountForCart(
            cart,
            discount ?? null,
        )

        return {
            saleSavings,
            discountCodeSavings: discountCodeSavings + pageFixedSavings,
            totalSavings: totalSavings + pageFixedSavings,
            totalItemCount,
            hasSaleSavings: saleSavings > 0,
            hasDiscountCode: discountCodeSavings + pageFixedSavings > 0,
            hasAnySavings: totalSavings + pageFixedSavings > 0,
        }
    }, [cart, discount, pricingContext])
}

export function useItemDiscount(
    item: CartItem,
    landingPageLinks?: ApiShopperLandingPageLink[],
    discount?: CartItemPricingContext['discount'],
): ItemDiscount {
    const pricingContext = useMemo(
        (): CartItemPricingContext | undefined =>
            landingPageLinks
                ? { landingPageLinks, discount }
                : discount
                  ? { discount }
                  : undefined,
        [landingPageLinks, discount],
    )

    return useMemo(() => {
        const totalSavings = calculateItemTotalSavings(item, pricingContext)
        const discountCodeSavings = calculateItemDiscountCodeSavings(item)
        const applicableDiscountCodes = getItemAppliedDiscountCodes(item) ?? []
        const hasDiscountCode =
            !!applicableDiscountCodes.length && !!discountCodeSavings
        const comparePrice = getEffectiveListPrice(item, pricingContext)
        const hasDiscount = totalSavings > 0
        const hasSaleSavings = totalSavings - discountCodeSavings > 0

        return {
            hasDiscount,
            hasDiscountCode,
            hasSaleSavings,
            comparePrice,
            applicableDiscountCodes,
        }
    }, [item, pricingContext])
}
